import { useEffect, useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { PipelineChatProposal } from '@/types/ai-pipeline';

interface ProposalDiffModalProps {
  proposal: PipelineChatProposal | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (selectedOperationIndexes: number[]) => void | Promise<void>;
  onReject: () => void | Promise<void>;
  isSubmitting?: boolean;
}

function titleCase(value: string): string {
  return value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function ProposalDiffModal({
  proposal,
  open,
  onOpenChange,
  onApply,
  onReject,
  isSubmitting = false,
}: ProposalDiffModalProps) {
  const [selected, setSelected] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!proposal || !open) return;
    setSelected(new Set(proposal.operations.map((_, index) => index)));
  }, [open, proposal]);

  const selectedIndexes = useMemo(
    () => Array.from(selected).sort((a, b) => a - b),
    [selected],
  );

  const opTypeCounts = useMemo(() => {
    if (!proposal) return {};

    return proposal.operations.reduce<Record<string, number>>((acc, operation) => {
      acc[operation.op] = (acc[operation.op] ?? 0) + 1;
      return acc;
    }, {});
  }, [proposal]);

  if (!proposal) return null;

  const toggleIndex = (index: number) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>Proposal Diff Review</DialogTitle>
          <DialogDescription>
            Review operation patch sebelum apply ke pipeline.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto pr-1">
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">Version {proposal.basePipelineVersion}</Badge>
            <Badge variant="outline">{proposal.operations.length} operations</Badge>
            <Badge variant="outline">{proposal.chartSpecs.length} chart specs</Badge>
          </div>

          <div className="space-y-2 rounded-md border bg-card p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Operation Mix
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(opTypeCounts).map(([operationType, count]) => (
                <Badge key={operationType} variant="secondary">
                  {titleCase(operationType)}: {count}
                </Badge>
              ))}
            </div>
          </div>

          <div className="space-y-2 rounded-md border p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Operations
            </p>
            <div className="space-y-2">
              {proposal.operations.map((operation, index) => {
                const label = `Operation ${index + 1}: ${titleCase(operation.op)}`;
                const checkboxId = `operation-${index + 1}`;

                return (
                  <div key={`${operation.op}-${index}`} className="rounded-md border bg-muted/20 p-2">
                    <div className="flex items-start gap-2">
                      <Checkbox
                        id={checkboxId}
                        checked={selected.has(index)}
                        onCheckedChange={() => toggleIndex(index)}
                        aria-label={label}
                      />
                      <div className="min-w-0 flex-1">
                        <label htmlFor={checkboxId} className="cursor-pointer text-sm font-medium">
                          {label}
                        </label>
                        <pre className="mt-2 overflow-x-auto rounded bg-background p-2 text-[11px] leading-5 text-muted-foreground">
                          {JSON.stringify(operation.payload, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onReject()}
            disabled={isSubmitting}
          >
            Reject
          </Button>
          <Button
            onClick={() => onApply(selectedIndexes)}
            disabled={isSubmitting || selectedIndexes.length === 0}
          >
            Apply Selected
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
